import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'gatsby'
import styled from 'styled-components'
import delay from 'lodash.delay'
import media from '../styles/media'
import Colors from '../styles/Colors'
import { ReactComponent as LogoLightSVG } from '../assets/svg/LogoHeader.svg'
import { ReactComponent as MenuCloseSVG } from '../assets/svg/menuClose.svg'
import gsap from 'gsap'

type Props = {
  menuOpen: boolean
  setMenuOpen: (boolean) => void
  isDarkMode?: boolean
}

const Header: React.FC<Props> = ({ menuOpen, setMenuOpen, isDarkMode }) => {
  const isAnimating = useRef(false)

  const toggleMenu = useCallback(() => {
    if (isAnimating.current) {
      return
    }
    isAnimating.current = true
    const time = menuOpen ? 1000 : 800
    setMenuOpen(!menuOpen)
    delay(() => {
      isAnimating.current = false
    }, time)
  }, [menuOpen])

  return (
    <Wrapper className="header" isDarkMode={isDarkMode}>
      <Link to="/" aria-label="Home">
        <Logo />
      </Link>
      <TextContainer
        isDarkMode={isDarkMode}
        open={menuOpen}
        className="header-menu__button"
        onClick={toggleMenu}
      >
        <MenuText className="menu-header__menu" open={menuOpen}>
          Menu
        </MenuText>
        <MenuX className="menu-header__x" open={menuOpen} />
      </TextContainer>
    </Wrapper>
  )
}

const Wrapper = styled.header<{ isDarkMode: boolean }>`
  position: fixed;
  top: 0;
  width: 100%;
  height: 96px;
  z-index: 20;
  background: ${(props) =>
    props.isDarkMode ? Colors.softBlack : Colors.whiteSmoke};
  border-bottom: 1px solid
    ${(props) => (props.isDarkMode ? Colors.whiteSmoke : Colors.softBlack)};
  a {
    color: ${(props) =>
      props.isDarkMode ? Colors.whiteSmoke : Colors.softBlack};
  }
  ${media.tabletLandscape} {
    height: 48px;
  }
`

const Logo = styled(LogoLightSVG)`
  position: absolute;
  width: 113px;
  left: 96px;
  top: 28px;
  ${media.tabletLandscape} {
    width: 68px;
    top: 12px;
    left: 72px;
  }
  ${media.tablet} {
    left: 48px;
  }
  ${media.mobile} {
    left: 12px;
  }
`

const TextContainer = styled.button<{ open: boolean; isDarkMode: boolean }>`
  top: 0;
  background: none;
  color: inherit;
  border: none;
  padding: 0;
  font: inherit;
  cursor: pointer;
  outline: inherit;
  position: absolute;
  right: 0;
  width: ${(props) => (props.open ? '839px' : '95px')};
  padding-left: ${(props) => (props.open ? '744px' : '0')};
  transition-property: width, padding;
  transition-timing-function: ${(props) =>
    props.open
      ? 'cubic-bezier(0.12, 1, 0.72, 1)'
      : 'cubic-bezier(0.72, 0, 0.12, 1)'};
  transition-duration: ${(props) => (props.open ? '800ms' : '1000ms')};
  height: 100%;
  color: ${Colors.softBlack};
  border-left: 1px solid
    ${(props) => (props.isDarkMode ? Colors.whiteSmoke : Colors.softBlack)};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) =>
    props.isDarkMode ? Colors.whiteSmoke : Colors.softBlack};

  ${media.tabletLandscape} {
    border: none;
  }
`

const MenuText = styled.p<{ open: boolean }>`
  font-weight: 500;
  position: absolute;
  transition-property: opacity;
  transition-timing-function: ${(props) =>
    props.open
      ? 'cubic-bezier(0.12, 1, 0.72, 1)'
      : 'cubic-bezier(0.72, 0, 0.12, 1)'};
  transition-duration: ${(props) => (props.open ? '800ms' : '1000ms')};
  opacity: ${(props) => (props.open ? 0 : 1)};
  ${media.tabletLandscape} {
    font-size: 16px;
    line-height: 16px;
    top: 15px;
    right: 12px;
  }
`

const MenuX = styled(MenuCloseSVG)<{ open: boolean }>`
  position: absolute;
  width: 24px;
  transition-timing-function: ${(props) =>
    props.open
      ? 'cubic-bezier(0.12, 1, 0.72, 1)'
      : 'cubic-bezier(0.72, 0, 0.12, 1)'};
  transition-duration: ${(props) => (props.open ? '800ms' : '1000ms')};
  opacity: ${(props) => (props.open ? 1 : 0)};
  .mc-left__x,
  .mc-right__x {
    transition-timing-function: ${(props) =>
      props.open
        ? 'cubic-bezier(0.12, 1, 0.72, 1)'
        : 'cubic-bezier(0.72, 0, 0.12, 1)'};
    transition-duration: ${(props) => (props.open ? '800ms' : '1000ms')};
    transform-origin: 50% 50%;
  }
  .mc-left__x {
    transform: rotate(${(props) => (props.open ? '0deg' : '-45deg')});
  }
  .mc-right__x {
    transform: rotate(${(props) => (props.open ? '0deg' : '45deg')});
  }
  ${media.tabletLandscape} {
    top: 10px;
    right: 18px;
  }
`

export default Header
